import {PageHeader} from '../ui/PageHeader';
import {useEffect,useState} from 'react';
import {useNavigate,useParams} from 'react-router';
import {api,ApiError} from '../api';
import {useStaffSession} from '../ui/StaffSession';
import {withBuildingScope} from '../ui/building-scope';
import {Link} from '../ui/ScopedLink';
import {ErrorState,Loading} from '../ui/State';

type Preview={id:string;workNumber:string;title:string;version:number;sourceBuilding:{id:string;name:string};
 targetBuilding:{id:string;name:string}|null;blockers:string[];canCorrect:boolean;sourceHash:string};
type Change={sourceBuildingId:string;targetBuildingId:string;expectedVersion:number;sourceHash:string;
 reason:string;confirm:true;idempotencyKey:string};
export function WorkBuildingCorrection(){
 const {id}=useParams(),session=useStaffSession(),navigate=useNavigate();
 const building=session?.effectiveScope.kind==='building'?session.effectiveScope.id:undefined;
 const [target,setTarget]=useState(''),[reason,setReason]=useState(''),[data,setData]=useState<Preview>();
 const [error,setError]=useState<unknown>(),[busy,setBusy]=useState(false),[pending,setPending]=useState<Change>(),[reload,setReload]=useState(0);
 useEffect(()=>{
  const controller=new AbortController();setError(undefined);
  const path=withBuildingScope(`/api/work/cases/${id}/building-correction`,building);
  void api<Preview>(path+(target?(path.includes('?')?'&':'?')+'targetBuildingId='+encodeURIComponent(target):''),{signal:controller.signal,cache:'no-store'})
   .then(value=>{if(!controller.signal.aborted)setData(value)}).catch(cause=>{if(!controller.signal.aborted)setError(cause)});
  return()=>controller.abort();
 },[id,building,target,reload]);
 async function save(){
  if(busy||(!pending&&(!data?.canCorrect||data.targetBuilding?.id!==target)))return;
  const body=pending??{sourceBuildingId:data!.sourceBuilding.id,targetBuildingId:target,expectedVersion:data!.version,
   sourceHash:data!.sourceHash,reason,confirm:true as const,idempotencyKey:crypto.randomUUID()};
  setPending(body);setBusy(true);setError(undefined);
  try{
   const saved=await api<{id:string;developmentId:string}>(withBuildingScope(`/api/work/cases/${id}/building-correction`,building),{method:'POST',body:JSON.stringify(body)});
   setPending(undefined);navigate(withBuildingScope(`/work/cases/${saved.id}`,saved.developmentId),{replace:true});
  }catch(cause){
   setError(cause);
   if(cause instanceof ApiError&&[400,401,403,404,409,422,423].includes(cause.statusCode))setPending(undefined);
  }finally{setBusy(false)}
 }
 return <div className="work-record-page source-configuration-workspace">
  <Link to={withBuildingScope(`/work/cases/${id}`,building)}>Back to work</Link>
  <PageHeader title="Correct building"/>
  {error!==undefined&&<ErrorState error={error}/>}
  {pending&&<p role="status">The save outcome is not yet confirmed. Retry the same correction to check its saved result.</p>}
  {!data&&!error&&<Loading/>}
  {data&&<>
   <p><strong>{data.workNumber}: {data.title}</strong></p><p>Recorded building: {data.sourceBuilding.name}</p>
   <form className="form" onSubmit={event=>{event.preventDefault();void save()}}>
    <fieldset className="form" disabled={busy||Boolean(pending)}><legend>Corrected location</legend>
     <label>Correct building<select required value={target} onChange={event=>setTarget(event.target.value)}>
      <option value="">Choose a building</option>
      {session?.buildings.filter(b=>b.id!==data.sourceBuilding.id).map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
     </select></label>
     <label>Reason for correction<textarea required minLength={10} maxLength={2000} value={reason} onChange={event=>setReason(event.target.value)}/></label>
    </fieldset>
    {data.blockers.length>0?<section aria-label="Correction needs review"><h2>Separate review needed</h2>
     <ul>{data.blockers.map(message=><li key={message}>{message}</li>)}</ul></section>:data.targetBuilding&&
     <p>Change {data.sourceBuilding.name} to {data.targetBuilding.name}. No linked records will be moved. Original history is retained.</p>}
    {pending?<button type="button" disabled={busy} onClick={()=>void save()}>Retry correction</button>:
     <button disabled={busy||!data.canCorrect||data.targetBuilding?.id!==target||reason.trim().length<10}>Correct building</button>}
    {!pending&&<button type="button" className="secondary" disabled={busy} onClick={()=>setReload(n=>n+1)}>Refresh preview</button>}
   </form>
  </>}
 </div>;
}
